import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../../auth/AuthContext";
import { useLang } from "../../../i18n/LanguageContext";
import { dreamsClient } from "../api/dreamsClient";
import {
  run4EAnalysisFlow,
  run4EFollowupFlow,
} from "../api/fourEFlow";
import { fourEClient } from "../api/fourEClient";
import { runProfileExtractFlow } from "../api/profileExtractFlowV2";
import { syncProfileContinuityFromRest } from "../utils/profileContinuityRest";
import { extractDreamText, sanitizeDreamHtml } from "../utils/safeDreamHtml";

const ANALYSIS_SOURCE_LOCAL_MOCK = "local_mock";
const ANALYSIS_SOURCE_BACKEND_4E = "backend_4e";
const ANALYSIS_SOURCE_BACKEND_FALLBACK = "backend_error_fallback";

const ANALYSIS_MODE_LABELS = {
  ai: "AI",
  jung: "Jung",
  freud: "Freud",
};

const getDreamViewCopy = (lang) => ({
  e2eLockedBody: lang === "en" ? "<p><em>🔒 This dream is encrypted (E2E).</em></p>" : "<p><em>🔒 Ten sen jest zaszyfrowany (E2E).</em></p>",
  legacyBody: lang === "en"
    ? "<p><em>⚠️ This entry comes from the old storage and has no E2E pack. The new Morfeusz does not show its contents.</em></p>"
    : "<p><em>⚠️ Ten wpis pochodzi ze starego zapisu i nie ma paczki E2E. Nie pokazuję treści w nowym Morfeuszu.</em></p>",
  tierLabels: {
    fast: lang === "en" ? "Quick" : "Szybka",
    deep: lang === "en" ? "Deep" : "Głęboka",
  },
  sourceLabels: {
    local_mock: lang === "en" ? "Local mock" : "Mock lokalny",
    backend_4e: lang === "en" ? "4E backend" : "Backend 4E",
    backend_error_fallback: lang === "en" ? "Error fallback" : "Fallback po błędzie",
  },
  untitled: lang === "en" ? "Untitled dream" : "Sen bez tytułu",
  saved: lang === "en" ? "Dream saved" : "Sen zapisany",
  oldSave: lang === "en" ? "⚠️ OLD SAVE" : "⚠️ STARY ZAPIS",
  noAnalysis: lang === "en" ? "no analysis" : "brak analizy",
  noData: lang === "en" ? "no data" : "brak danych",
  ready: lang === "en" ? "ready" : "gotowy",
  waiting: lang === "en" ? "waiting" : "oczekuje",
  none: lang === "en" ? "none" : "brak",
  error: lang === "en" ? "error" : "błąd",
  debugAria: lang === "en" ? "4E technical status" : "Status techniczny 4E",
  debugApi: lang === "en" ? "4E API" : "4E API",
  debugConfigured: lang === "en" ? "configured" : "skonfigurowane",
  debugLocalMock: lang === "en" ? "local mock" : "mock lokalny",
  debugAnalysis: lang === "en" ? "Analysis" : "Analiza",
  debugModel: lang === "en" ? "Model" : "Model",
  debugPrompt: lang === "en" ? "Prompt" : "Prompt",
  debugBackend: lang === "en" ? "Backend" : "Backend",
  debugLimit: lang === "en" ? "Limit" : "Limit",
  debugProfile: lang === "en" ? "Profile" : "Profil",
  debugExtract: lang === "en" ? "Extract" : "Extract",
  debugFollowup: lang === "en" ? "Follow-up" : "Follow-up",
  profileGateMissing: lang === "en" ? "none" : "brak",
  promptMissing: lang === "en" ? "none" : "brak",
  followupMissing: lang === "en" ? "none" : "brak",
  gateEyebrow: lang === "en" ? "Dream gate" : "Senna brama",
  gateTitlePre: lang === "en" ? "Welcome to" : "Witaj w",
  gateSubtitle: lang === "en" ? "Sign in to open your Dream Codex." : "Zaloguj się, aby otworzyć swoją Księgę Snów.",
  signIn: lang === "en" ? "🔑 Sign in" : "🔑 Zaloguj się",
  register: lang === "en" ? "✨ Create account" : "✨ Załóż konto",
  home: lang === "en" ? "🏠 Home" : "🏠 Strona główna",
  notFoundTitle: lang === "en" ? "Dream not found" : "Sen nie został znaleziony",
  notFoundMessage: lang === "en" ? "Return to the Room or add a new dream." : "Wróć do Pokoiku albo dodaj nowy sen.",
  addDream: lang === "en" ? "Add new dream" : "Dodaj nowy sen",
  dreamList: lang === "en" ? "Dream list" : "Lista snów",
  backRoom: lang === "en" ? "Back to Room" : "Wróć do Pokoiku",
  loadingTitle: lang === "en" ? "Opening dream" : "Otwieram sen",
  loadingSubtitle: lang === "en" ? "Fetching the entry from the Dream Codex." : "Pobieram wpis z Księgi Snów.",
  fetchError: lang === "en" ? "Could not fetch the dream." : "Nie udało się pobrać snu.",
  unlockHint: lang === "en" ? "Unlock the vault in the Room to see this dream." : "Odblokuj sejf w Pokoiku, aby zobaczyć treść tego snu.",
  decryptFailed: lang === "en" ? "Could not decrypt the dream with this vault." : "Nie udało się odszyfrować snu tym sejfem.",
  analysisSection: lang === "en" ? "Dream analysis" : "Analiza snu",
  disclaimer: lang === "en"
    ? "Treat interpretations as hypotheses and material for self-reflection, not as a diagnosis."
    : "Interpretacje traktuj jako hipotezy i materiał do autorefleksji, nie jako diagnozę.",
  depth: lang === "en" ? "Depth:" : "Głębia:",
  fastButton: lang === "en" ? "⚡ Quick" : "⚡ Szybka",
  deepButton: lang === "en" ? "🔋 Deep" : "🔋 Głęboka",
  analyzing: lang === "en" ? "Analyzing…" : "Analizuję…",
  limits: lang === "en" ? "Limits:" : "Limity:",
  oldNoE2E: lang === "en" ? "this entry comes from the old storage and has no E2E pack." : "ten wpis pochodzi ze starego zapisu i nie ma paczki E2E.",
  unlockToAnalyze: lang === "en" ? "unlock the vault to run the analysis." : "odblokuj sejf, aby wykonać analizę.",
  preparing: lang === "en" ? "preparing a result in the 4E structure…" : "przygotowuję wynik w strukturze 4E…",
  profileUpdated: lang === "en" ? "updated" : "zaktualizowany",
  profileSkipped: lang === "en" ? "skipped" : "pominięty",
  saved4E: lang === "en" ? "saved the 4E analysis result" : "zapisano wynik analizy 4E",
  backendFallback: lang === "en" ? "the 4E backend did not respond correctly; local fallback was saved." : "backend 4E nie odpowiedział poprawnie, zapisano fallback lokalny.",
  localDraft: lang === "en" ? "saved a local draft. The 4E backend will be connected in the next stage." : "zapisano szkic lokalnie. Backend 4E zostanie podłączony w następnym etapie.",
  saveResultFailed: lang === "en" ? "could not save the result." : "nie udało się zapisać wyniku.",
  analysisResult: lang === "en" ? "Analysis result" : "Wynik analizy",
  followupSummary: lang === "en" ? "Additional question for the analysis" : "Dodatkowe pytanie do analizy",
  followupLabel: lang === "en" ? "Ask one question related to this dream or analysis." : "Zadaj jedno pytanie związane z tym snem lub analizą.",
  followupPlaceholder: lang === "en" ? "For example: what could this symbol mean?" : "Np. co może oznaczać ten symbol?",
  askQuestion: lang === "en" ? "Ask question" : "Zadaj pytanie",
  answering: lang === "en" ? "Answering…" : "Odpowiadam…",
  runAnalysisFirst: lang === "en" ? "Run an analysis of this dream first." : "Najpierw wykonaj analizę tego snu.",
  followupUsed: lang === "en" ? "The additional question for this analysis has already been used." : "Dodatkowe pytanie do tej analizy już wykorzystano.",
  enterQuestion: lang === "en" ? "Enter a question." : "Wpisz pytanie.",
  followupPreparing: lang === "en" ? "Preparing the follow-up answer…" : "Przygotowuję odpowiedź follow-up…",
  followupSaved: lang === "en" ? "Saved the 4E follow-up answer." : "Zapisano odpowiedź follow-up 4E.",
  followupFallback: lang === "en" ? "The follow-up backend did not respond correctly; local fallback was saved." : "Backend follow-up nie odpowiedział poprawnie, zapisano fallback lokalny.",
  followupLocal: lang === "en" ? "Saved the follow-up answer locally. Q&A backend will be connected in the next stage." : "Zapisano odpowiedź follow-up lokalnie. Backend Q&A zostanie podłączony w kolejnym etapie.",
  followupFailed: lang === "en" ? "Could not save the answer." : "Nie udało się zapisać odpowiedzi.",
  followupAnswerTitle: lang === "en" ? "Additional question — answer" : "Dodatkowe pytanie — odpowiedź",
  question: lang === "en" ? "Question:" : "Pytanie:",
  shareTitle: lang === "en" ? "Share on forum" : "Udostępnij na forum",
  shareDreamBody: lang === "en" ? "Dream body" : "Treść snu",
  shareAnalysis: lang === "en" ? "Analysis" : "Analiza",
  shareHint: lang === "en" ? "Sharing will be connected after the forum logic is migrated from 4E." : "Udostępnianie zostanie podłączone po migracji logiki forum z 4E.",
  editDream: lang === "en" ? "Edit dream" : "Edytuj sen",
});

const scrollToSelector = (selector) => {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  window.requestAnimationFrame(() => {
    const node = document.querySelector(selector);
    if (!node) return;
    const headerOffset = document.querySelector("header")?.getBoundingClientRect?.().height || 0;
    const top = node.getBoundingClientRect().top + window.scrollY - Math.max(0, headerOffset + 18);
    window.scrollTo({ top: Math.max(0, top), left: 0, behavior: "smooth" });
  });
};

const escapeHtml = (value) =>
  String(value || "").replace(/[&<>"']/g, (char) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#039;",
  })[char]);

const normalizeArray = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value === "string" && value.trim()) {
    return value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }
  return [];
};

const normalizeTier = (value) => (value === "deep" ? "deep" : "fast");
const getDreamE2E = (dream) => dream?.e2e || { iv: "", ct: "", aad: "" };
const hasRealE2EPack = (dream) => {
  const pack = getDreamE2E(dream);
  return Boolean(pack.iv && pack.ct);
};

const normalizeAnalysis = (dream) => {
  const raw = dream.analysis || dream.md4e_analysis || null;
  const legacy = dream.analysisHtml || dream._analiza_ai || "";

  if (raw && typeof raw === "object") {
    const tier = normalizeTier(raw.tier || raw.modelTier || raw.payloadDraft?.model || dream.analysisTier || "fast");
    return {
      source: raw.source || ANALYSIS_SOURCE_BACKEND_4E,
      mode: raw.mode || dream.analysisMode || "ai",
      tier,
      modelTier: tier,
      model: raw.model || raw.modeLabel || "4E",
      html: sanitizeDreamHtml(raw.html || legacy),
      plainText: raw.plainText || extractDreamText(raw.html || legacy),
      createdAt: raw.createdAt || "",
      profileUsed: Boolean(raw.profileUsed),
      profileVersion: raw.profileVersion ?? null,
      profileSnapshotMeta: raw.profileSnapshotMeta || null,
      profileGate: raw.profileGate || null,
      payloadDraft: raw.payloadDraft || null,
      backendMeta: raw.backendMeta || null,
      promptBand: raw.promptBand || "",
    };
  }

  if (legacy) {
    return {
      source: ANALYSIS_SOURCE_LOCAL_MOCK,
      mode: dream.analysisMode || "ai",
      tier: normalizeTier(dream.analysisTier || "fast"),
      modelTier: normalizeTier(dream.analysisTier || "fast"),
      model: "legacy-local",
      html: sanitizeDreamHtml(legacy),
      plainText: extractDreamText(legacy),
      createdAt: dream.analysisCreatedAt || "",
      profileUsed: false,
      profileVersion: null,
      profileSnapshotMeta: null,
      profileGate: null,
      payloadDraft: null,
      backendMeta: null,
      promptBand: "",
    };
  }

  return null;
};

const normalizeFollowup = (dream) => {
  const raw = dream.followup || dream.md_followup || null;
  const legacyQ = dream._followup_q || "";
  const legacyA = dream._followup_a || "";

  if (raw && typeof raw === "object") {
    return {
      source: raw.source || ANALYSIS_SOURCE_BACKEND_4E,
      q: String(raw.q || legacyQ || ""),
      a: String(raw.a || raw.answer || legacyA || ""),
      used: Boolean(raw.used || raw.q || raw.a || legacyA),
      createdAt: raw.createdAt || raw.at || dream._followup_at || "",
      profileUsed: Boolean(raw.profileUsed),
      profileGate: raw.profileGate || null,
      payloadDraft: raw.payloadDraft || null,
      backendMeta: raw.backendMeta || null,
    };
  }

  if (legacyQ || legacyA) {
    return {
      source: ANALYSIS_SOURCE_LOCAL_MOCK,
      q: String(legacyQ),
      a: String(legacyA),
      used: Boolean(legacyA),
      createdAt: dream._followup_at || "",
      profileUsed: false,
      profileGate: null,
      payloadDraft: null,
      backendMeta: null,
    };
  }

  return null;
};

const normalizeDream = (dream, decryptedBodyHtml = "", copy) => {
  if (!dream) return null;
  const e2e = getDreamE2E(dream);
  const hasE2E = hasRealE2EPack(dream);
  const bodyHtml = hasE2E ? (decryptedBodyHtml || "") : "";
  const date = dream.date || dream.meta?.date || dream.sen_data || "";
  const time = dream.time || dream.meta?.time || dream.sen_godzina || "";
  const symbols = normalizeArray(dream.symbols || dream.meta?.symbols || dream.sen_symbole || "");
  const emotions = normalizeArray(dream.emotions || dream.meta?.emotions || dream.sen_emocje || []);

  return {
    id: dream.id || dream.wpId,
    title: dream.title || dream.sen_tytul || dream.meta?.title || copy.untitled,
    date,
    time,
    dateHuman: [date, time].filter(Boolean).join(" · ") || copy.saved,
    bodyHtml: sanitizeDreamHtml(bodyHtml),
    bodyText: extractDreamText(bodyHtml),
    notesHtml: "",
    analysis: normalizeAnalysis(dream),
    followup: normalizeFollowup(dream),
    saveMode: hasE2E ? "e2e" : "legacy",
    saveBadge: hasE2E ? "🔐 E2E" : copy.oldSave,
    hasE2E,
    symbols,
    emotions,
    e2e,
  };
};

const getDreamIdFromHash = () => {
  if (typeof window === "undefined") return "";
  const hash = window.location.hash || "";
  const match = hash.match(/^#\/sen\/([^?]+)/);
  return match ? decodeURIComponent(match[1]) : "";
};

const isVaultUnlocked = () => {
  if (typeof window === "undefined") return false;
  try {
    return Boolean(window.MDVaultSession?.getState?.()?.unlocked || window.MDVaultSession?.isUnlocked?.());
  } catch (_) {
    return false;
  }
};

const getProfileExtractStatus = (analysis, copy) => {
  const extract = analysis?.backendMeta?.profileExtract;
  if (!analysis) return copy.noAnalysis;
  if (!extract) return copy.noData;
  if (extract.ok) return copy.ready;
  return [extract.stage, extract.reason].filter(Boolean).join(" / ") || copy.waiting;
};

const getBackendErrorStatus = (analysis, copy) => {
  const error = analysis?.backendMeta?.backendError;
  if (!error) return copy.none;
  return [error.code, error.status].filter(Boolean).join(" / ") || copy.error;
};

const formatUsageLeft = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  return String(value);
};

const Debug4EStatus = ({ analysis, followup, copy }) => {
  const apiConfigured = fourEClient.isConfigured();
  const source = analysis?.source || "none";
  const profileGateReason = analysis?.profileGate?.reason || copy.profileGateMissing;
  const promptBand = analysis?.promptBand || analysis?.payloadDraft?.client?.appVersion || copy.promptMissing;
  const followupSource = followup?.source || copy.followupMissing;
  const backendError = getBackendErrorStatus(analysis, copy);
  const tier = analysis?.tier || analysis?.modelTier || analysis?.backendMeta?.meta?.model || "fast";
  const usageLeft = analysis?.backendMeta?.usage?.left || null;

  return (
    <aside className="md-4e-debug" data-ui="md4e-debug-status" aria-label={copy.debugAria}>
      <div className="md-4e-debug-row"><span>{copy.debugApi}</span><strong>{apiConfigured ? copy.debugConfigured : copy.debugLocalMock}</strong></div>
      <div className="md-4e-debug-row"><span>{copy.debugAnalysis}</span><strong>{copy.sourceLabels[source] || source}</strong></div>
      <div className="md-4e-debug-row"><span>{copy.debugModel}</span><strong>{copy.tierLabels[tier] || tier}</strong></div>
      <div className="md-4e-debug-row"><span>{copy.debugPrompt}</span><strong>{promptBand}</strong></div>
      <div className="md-4e-debug-row"><span>{copy.debugBackend}</span><strong>{backendError}</strong></div>
      <div className="md-4e-debug-row"><span>{copy.debugLimit}</span><strong>⚡ {formatUsageLeft(usageLeft?.fast)} · 🔋 {formatUsageLeft(usageLeft?.deep)}</strong></div>
      <div className="md-4e-debug-row"><span>{copy.debugProfile}</span><strong>{profileGateReason}</strong></div>
      <div className="md-4e-debug-row"><span>{copy.debugExtract}</span><strong>{getProfileExtractStatus(analysis, copy)}</strong></div>
      <div className="md-4e-debug-row"><span>{copy.debugFollowup}</span><strong>{copy.sourceLabels[followupSource] || followupSource}</strong></div>
    </aside>
  );
};

const PokoikGate = ({ copy }) => (
  <section className="mdPage" data-ui="gate">
    <div className="mdPage__inner">
      <div>
        <p className="mdPage__eyebrow">{copy.gateEyebrow}</p>
        <h1 className="mdPage__title">{copy.gateTitlePre} <em>Morfeusz Dreams</em></h1>
        <p className="mdPage__subtitle">{copy.gateSubtitle}</p>
      </div>
      <div className="mdCard">
        <div className="mdRow">
          <a className="mdBtn mdBtn--primary mdBtn--row" data-md-login="1" href="#/zaloguj?redirect_to=/moje-sny">{copy.signIn}</a>
          <a className="mdBtn mdBtn--row" href="#/rejestracja?redirect_to=/moje-sny">{copy.register}</a>
          <a className="mdBtn mdBtn--row" href="#top">{copy.home}</a>
        </div>
      </div>
    </div>
  </section>
);

const DreamNotFound = ({ message, copy }) => (
  <section className="dream-container" id="widok-snu" data-ui="dream-view-root">
    <header className="dream-header">
      <div className="dream-icon" aria-hidden="true">🌙</div>
      <h1 className="dream-title">{copy.notFoundTitle}</h1>
      <div className="dream-date">{message || copy.notFoundMessage}</div>
    </header>
    <div className="dream-bottom-actions" data-ui="dream-actions-row">
      <a className="md-btn md-primary" href="#/dodaj-sen">{copy.addDream}</a>
      <a className="md-btn md-secondary" href="#/pokoik">{copy.backRoom}</a>
      <a className="md-btn md-secondary" href="#/moje-sny">{copy.dreamList}</a>
    </div>
  </section>
);

const DreamLoading = ({ copy }) => (
  <section className="dream-container" id="widok-snu" data-ui="dream-view-loading">
    <header className="dream-header">
      <div className="dream-icon" aria-hidden="true">🌙</div>
      <h1 className="dream-title">{copy.loadingTitle}</h1>
      <div className="dream-date">{copy.loadingSubtitle}</div>
    </header>
  </section>
);

export const DreamViewPage = () => {
  const { lang } = useLang();
  const { isAuthenticated, authReady } = useAuth();
  const copy = useMemo(() => getDreamViewCopy(lang), [lang]);
  const [dreamRaw, setDreamRaw] = useState(null);
  const [decryptedBodyHtml, setDecryptedBodyHtml] = useState("");
  const [e2eStatus, setE2eStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [analysisStatus, setAnalysisStatus] = useState("");
  const [followupStatus, setFollowupStatus] = useState("");
  const [followupQuestion, setFollowupQuestion] = useState("");
  const [analysisTier, setAnalysisTier] = useState("fast");
  const [selectedAnalysisMode, setSelectedAnalysisMode] = useState("ai");
  const [busyMode, setBusyMode] = useState("");
  const [followupBusy, setFollowupBusy] = useState(false);
  const dreamId = getDreamIdFromHash();

  useEffect(() => {
    let mounted = true;

    const loadDream = async () => {
      if (!authReady) return;
      if (!isAuthenticated) {
        setDreamRaw(null);
        setDecryptedBodyHtml("");
        setLoading(false);
        return;
      }

      setLoading(true);
      setLoadError("");
      setDecryptedBodyHtml("");
      setE2eStatus("");
      try {
        const response = await dreamsClient.get(dreamId);
        if (!mounted) return;
        setDreamRaw(response.dream || null);
      } catch (error) {
        if (!mounted) return;
        setDreamRaw(null);
        setLoadError(error?.message || copy.fetchError);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadDream();
    return () => {
      mounted = false;
    };
  }, [authReady, isAuthenticated, dreamId, copy.fetchError]);

  useEffect(() => {
    let cancelled = false;

    const decryptDream = async () => {
      const pack = dreamRaw?.e2e || {};
      if (!pack.iv || !pack.ct || decryptedBodyHtml) return;
      if (!isVaultUnlocked()) {
        setE2eStatus(copy.unlockHint);
        return;
      }

      try {
        const plain = await window.morfeuszE2E.decryptText(pack.iv, pack.ct, pack.aad || "");
        if (!cancelled) {
          setDecryptedBodyHtml(sanitizeDreamHtml(plain));
          setE2eStatus("");
        }
      } catch (_) {
        if (!cancelled) setE2eStatus(copy.decryptFailed);
      }
    };

    decryptDream();
    window.addEventListener("morfeusz:vault:session", decryptDream);
    return () => {
      cancelled = true;
      window.removeEventListener("morfeusz:vault:session", decryptDream);
    };
  }, [dreamRaw, decryptedBodyHtml, copy.unlockHint, copy.decryptFailed]);

  const dream = useMemo(() => normalizeDream(dreamRaw, decryptedBodyHtml, copy), [dreamRaw, decryptedBodyHtml, copy]);

  if (!authReady || loading) return <DreamLoading copy={copy} />;
  if (!isAuthenticated) return <PokoikGate copy={copy} />;
  if (!dream) return <DreamNotFound copy={copy} message={loadError || copy.notFoundMessage} />;

  const contentHtml = dream.hasE2E ? (dream.bodyHtml ? dream.bodyHtml : copy.e2eLockedBody) : copy.legacyBody;

  const updateDreamState = (patch) => {
    setDreamRaw((current) => ({ ...(current || {}), ...patch }));
  };

  const ensureProfileExtractAfterAnalysis = async ({ analysis, baseExtract }) => {
    await syncProfileContinuityFromRest().catch(() => null);
    return runProfileExtractFlow({
      dream,
      analysis,
      ticket: analysis?.backendMeta?.profileExtractTicket || "",
      backendExtract: baseExtract || null,
    });
  };

  const handleAnalysisClick = async (mode) => {
    const label = ANALYSIS_MODE_LABELS[mode] || "AI";
    const tierLabel = copy.tierLabels[analysisTier] || copy.tierLabels.fast;

    if (!dream.hasE2E) {
      setSelectedAnalysisMode(mode);
      setAnalysisStatus(`${label} · ${tierLabel}: ${copy.oldNoE2E}`);
      return;
    }

    if (!dream.bodyText) {
      setSelectedAnalysisMode(mode);
      setAnalysisStatus(`${label} · ${tierLabel}: ${copy.unlockToAnalyze}`);
      return;
    }

    const busyKey = `${analysisTier}:${mode}`;
    setSelectedAnalysisMode(mode);
    setBusyMode(busyKey);
    setAnalysisStatus(`${label} · ${tierLabel}: ${copy.preparing}`);

    try {
      const result = await run4EAnalysisFlow({ dream, mode, tier: analysisTier });
      const profileExtract = await ensureProfileExtractAfterAnalysis({ analysis: result.analysis, baseExtract: result.profileExtract });
      const analysisWithProfile = {
        ...result.analysis,
        backendMeta: {
          ...(result.analysis.backendMeta || {}),
          profileExtract,
        },
      };
      const saved = await dreamsClient.update(dream.id, {
        analysis: analysisWithProfile,
        followup: null,
      });
      updateDreamState(saved.dream || { analysis: analysisWithProfile, followup: null });
      setFollowupQuestion("");
      setFollowupStatus("");
      setAnalysisStatus(
        result.source === ANALYSIS_SOURCE_BACKEND_4E
          ? `${label} · ${tierLabel}: ${copy.saved4E}. ${copy.debugProfile}: ${profileExtract?.ok ? copy.profileUpdated : (profileExtract?.reason || copy.profileSkipped)}.`
          : result.source === ANALYSIS_SOURCE_BACKEND_FALLBACK
            ? `${label} · ${tierLabel}: ${copy.backendFallback}`
            : `${label} · ${tierLabel}: ${copy.localDraft}`,
      );
      scrollToSelector("#ai-wynik");
    } catch (error) {
      setAnalysisStatus(error?.message || `${label} · ${tierLabel}: ${copy.saveResultFailed}`);
    } finally {
      setBusyMode("");
    }
  };

  const handleFollowupSubmit = async (event) => {
    event.preventDefault();

    if (!dream.analysis?.html) {
      setFollowupStatus(copy.runAnalysisFirst);
      return;
    }

    if (dream.followup?.used) {
      setFollowupStatus(copy.followupUsed);
      return;
    }

    const q = followupQuestion.trim();
    if (!q) {
      setFollowupStatus(copy.enterQuestion);
      return;
    }

    setFollowupBusy(true);
    setFollowupStatus(copy.followupPreparing);

    try {
      const result = await run4EFollowupFlow({ dream, analysis: dream.analysis, question: q });
      const saved = await dreamsClient.update(dream.id, { followup: result.followup });
      updateDreamState(saved.dream || { followup: result.followup });
      setFollowupQuestion("");
      setFollowupStatus(
        result.source === ANALYSIS_SOURCE_BACKEND_4E
          ? copy.followupSaved
          : result.source === ANALYSIS_SOURCE_BACKEND_FALLBACK
            ? copy.followupFallback
            : copy.followupLocal,
      );
      scrollToSelector('[data-ui="md-followup-card"]');
    } catch (error) {
      setFollowupStatus(error?.message || copy.followupFailed);
    } finally {
      setFollowupBusy(false);
    }
  };

  const analysis = dream.analysis;
  const activeAnalysisMode = busyMode ? busyMode.split(":")[1] : (analysis?.mode || selectedAnalysisMode || "ai");
  const analysisLabel = analysis ? (ANALYSIS_MODE_LABELS[analysis.mode] || "AI") : "AI";
  const analysisTierLabel = analysis ? (copy.tierLabels[analysis.tier || analysis.modelTier] || copy.tierLabels.fast) : copy.tierLabels.fast;
  const usageLeft = analysis?.backendMeta?.usage?.left || null;
  const followup = dream.followup;
  const canRunAnalysis = Boolean(dream.hasE2E && dream.bodyText);

  return (
    <section
      className="dream-container"
      id="widok-snu"
      data-ui="dream-view-root"
      data-dream-id={dream.id}
      data-e2e={dream.hasE2E ? "1" : "0"}
      data-e2e-iv={dream.e2e.iv || ""}
      data-e2e-ct={dream.e2e.ct || ""}
      data-e2e-aad={dream.e2e.aad || ""}
    >
      <header className="dream-header">
        <div className="dream-icon" aria-hidden="true">🌙</div>
        <h1 className="dream-title">{dream.title}</h1>
        <div className="dream-date">{dream.dateHuman}</div>
        <div className="md-cta-row" data-ui="dream-save-badge-row">
          <span className="md-chip" data-ui="dream-save-badge">{dream.saveBadge}</span>
          {dream.emotions.map((emotion) => <span className="md-chip" key={emotion} data-ui="dream-emotion-badge">{emotion}</span>)}
          {dream.symbols.map((symbol) => <span className="md-chip" key={symbol} data-ui="dream-symbol-badge">{symbol}</span>)}
        </div>
      </header>

      {e2eStatus && <div className="md-alert md-info" data-ui="dream-e2e-status" role="status">{e2eStatus}</div>}

      <article className="dream-content-card" data-ui="md4e-dream-card">
        <div id="md4e-dream-body" className="dream-content" dangerouslySetInnerHTML={{ __html: contentHtml }} />
      </article>

      <section className="ai-analysis-section" data-ui="md4e-analysis-panel">
        <div className="ai-section-header">
          <span className="ai-analysis-icon" aria-hidden="true">✨</span>
          <h2>{copy.analysisSection}</h2>
        </div>
        <p className="ai-disclaimer">{copy.disclaimer}</p>
        <div className="md-4e-credits-line" data-ui="md4e-tier-line">
          <span className="lbl">{copy.depth}</span>
          <button type="button" className={`md-btn ${analysisTier === "fast" ? "md-primary" : "md-secondary"}`} disabled={!!busyMode} onClick={() => setAnalysisTier("fast")}>{copy.fastButton}</button>
          <button type="button" className={`md-btn ${analysisTier === "deep" ? "md-primary" : "md-secondary"}`} disabled={!!busyMode} onClick={() => setAnalysisTier("deep")}>{copy.deepButton}</button>
        </div>
        <div className="ai-analysis-buttons" data-ui="md4e-analysis-actions">
          <button type="button" className={`md-btn ${activeAnalysisMode === "ai" ? "md-primary" : "md-secondary"}`} disabled={!!busyMode || !canRunAnalysis} onClick={() => handleAnalysisClick("ai")}>{busyMode === `${analysisTier}:ai` ? copy.analyzing : "AI"}</button>
          <button type="button" className={`md-btn ${activeAnalysisMode === "jung" ? "md-primary" : "md-secondary"}`} disabled={!!busyMode || !canRunAnalysis} onClick={() => handleAnalysisClick("jung")}>{busyMode === `${analysisTier}:jung` ? copy.analyzing : "Jung"}</button>
          <button type="button" className={`md-btn ${activeAnalysisMode === "freud" ? "md-primary" : "md-secondary"}`} disabled={!!busyMode || !canRunAnalysis} onClick={() => handleAnalysisClick("freud")}>{busyMode === `${analysisTier}:freud` ? copy.analyzing : "Freud"}</button>
        </div>
        <div className="md-4e-credits-line" data-ui="md4e-credits-line">
          <span className="lbl">{copy.limits}</span>
          <strong>⚡ {formatUsageLeft(usageLeft?.fast)}</strong>
          <span>🔋 {formatUsageLeft(usageLeft?.deep)}</span>
        </div>
        {analysisStatus && <div className="md-alert md-info" data-ui="md4e-analysis-status" role="status">{analysisStatus}</div>}
      </section>

      <Debug4EStatus analysis={analysis} followup={followup} copy={copy} />

      <div id="ai-wynik" data-ui="md4e-analysis-result">
        {analysis && analysis.html && (
          <article className="dream-ai-analysis" data-analysis-source={analysis.source || "unknown"}>
            <div className="ai-analysis-header">
              <span className="ai-analysis-icon" aria-hidden="true">🌙</span>
              <h3>{copy.analysisResult}</h3>
              <span className="ai-model-badge md-chip" data-ui="md4e-analysis-mode">{analysisLabel} · {analysisTierLabel}</span>
            </div>
            <div className="ai-analysis-content" dangerouslySetInnerHTML={{ __html: analysis.html }} />
          </article>
        )}

        {analysis && !followup?.used && (
          <details data-ui="md-followup" open>
            <summary data-ui="md-followup-summary">{copy.followupSummary}</summary>
            <form data-ui="md-followup-box" onSubmit={handleFollowupSubmit}>
              <label htmlFor="md-followup-question">{copy.followupLabel}</label>
              <textarea id="md-followup-question" className="md-input" value={followupQuestion} onChange={(event) => setFollowupQuestion(event.target.value)} maxLength={800} placeholder={copy.followupPlaceholder} />
              <div data-ui="md-followup-actions">
                <button type="submit" className="md-btn md-primary" disabled={followupBusy}>{followupBusy ? copy.answering : copy.askQuestion}</button>
              </div>
              {followupStatus && <div className="md-hint" data-ui="md-followup-status">{followupStatus}</div>}
            </form>
          </details>
        )}

        {followup?.used && followup.a && (
          <article className="dream-ai-analysis" data-ui="md-followup-card" data-analysis-source={followup.source || "unknown"}>
            <div className="ai-analysis-header"><span className="ai-analysis-icon" aria-hidden="true">❓</span><h3>{copy.followupAnswerTitle}</h3></div>
            <div className="ai-analysis-content">
              <p><strong>{copy.question}</strong> {followup.q}</p>
              <hr />
              <p dangerouslySetInnerHTML={{ __html: escapeHtml(followup.a).replace(/\n/g, "<br>") }} />
            </div>
          </article>
        )}
      </div>

      <section className="dream-share-panel" data-ui="dream-share-panel">
        <div data-ui="share-header"><div data-ui="share-title"><span className="share-icon" aria-hidden="true">💬</span>{copy.shareTitle}</div></div>
        <div className="share-checkboxes">
          <label className="modern-checkbox"><input type="checkbox" disabled /><span className="label-text">{copy.shareDreamBody}</span></label>
          <label className="modern-checkbox"><input type="checkbox" disabled /><span className="label-text">{copy.shareAnalysis}</span></label>
        </div>
        <div className="md-hint">{copy.shareHint}</div>
      </section>

      <div className="dream-bottom-actions" data-ui="dream-actions-row">
  <a className="md-btn md-primary" href={`#/sen/${encodeURIComponent(dream.id)}?edit=1`}>{copy.editDream}</a>
  <a className="md-btn md-secondary" href="#/pokoik">{copy.backRoom}</a>
  <a className="md-btn md-secondary" href="#/moje-sny">{copy.dreamList}</a>
  <a className="md-btn md-secondary" href="#/dodaj-sen">{copy.addDream}</a>
</div>
    </section>
  );
};
